// src/pages/features/GeoRiskMapPage.jsx
// /features/geo-risk-map — unique product differentiator, highest-priority feature page
// Copy: docs/superpowers/marketing/geo-risk-map-feature-page-copy.md
import { useEffect } from 'react';

const S = {
  page: { background: '#0a0a0a', color: '#c9d1d9', fontFamily: "'DM Sans', system-ui, sans-serif", minHeight: '100vh' },
  nav: { padding: '20px 24px', borderBottom: '1px solid #1c2333' },
  navInner: { maxWidth: 1060, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  navLogo: { fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 800, fontSize: 18, color: '#c9d1d9', textDecoration: 'none' },
  navLink: { color: '#3ddc84', textDecoration: 'none', fontSize: 14 },

  hero: { padding: '80px 24px 64px', textAlign: 'center' },
  heroInner: { maxWidth: 800, margin: '0 auto' },
  badge: { display: 'inline-block', background: '#0E180F', color: '#3ddc84', border: '1px solid #2db870', borderRadius: 4, padding: '4px 12px', fontSize: 11, fontFamily: "'IBM Plex Mono', 'Courier New', monospace", letterSpacing: 1, marginBottom: 24 },
  h1: { fontSize: 'clamp(32px, 5vw, 56px)', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 800, color: '#c9d1d9', margin: '0 0 24px', lineHeight: 1.1 },
  heroSub: { fontSize: 18, color: '#8b949e', marginBottom: 40, lineHeight: 1.6, maxWidth: 640, margin: '0 auto 40px' },
  statRow: { display: 'flex', justifyContent: 'center', gap: 32, flexWrap: 'wrap', marginBottom: 48 },
  statItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#c9d1d9' },
  statDot: { width: 6, height: 6, borderRadius: '50%', background: '#3ddc84', flexShrink: 0 },
  ctaRow: { display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' },
  ctaPrimary: { display: 'inline-block', padding: '14px 32px', background: '#3ddc84', color: '#0a0a0a', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: 16, borderRadius: 8, textDecoration: 'none', cursor: 'pointer' },
  ctaSecondary: { display: 'inline-block', padding: '14px 32px', background: 'transparent', color: '#3ddc84', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: 16, borderRadius: 8, textDecoration: 'none', border: '1px solid #2db870', cursor: 'pointer' },

  section: { padding: '80px 24px' },
  sectionAlt: { padding: '80px 24px', background: '#080c12' },
  inner: { maxWidth: 1060, margin: '0 auto' },
  sectionLabel: { fontFamily: "'IBM Plex Mono', 'Courier New', monospace", fontSize: 11, color: '#3ddc84', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 },
  h2: { fontSize: 'clamp(24px, 4vw, 40px)', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 800, color: '#c9d1d9', margin: '0 0 16px', lineHeight: 1.2 },
  sectionIntro: { fontSize: 16, color: '#8b949e', marginBottom: 48, maxWidth: 680, lineHeight: 1.7 },

  grid3: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24 },
  card: { background: '#111111', border: '1px solid #1c2333', borderRadius: 12, padding: 32 },
  cardAccent: { background: '#111111', border: '1px solid #3ddc84', borderRadius: 12, padding: 32 },
  cardTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: 18, color: '#c9d1d9', marginBottom: 12 },
  cardBody: { fontSize: 14, color: '#8b949e', lineHeight: 1.7 },
  stepNum: { fontFamily: "'IBM Plex Mono', 'Courier New', monospace", fontSize: 11, color: '#3ddc84', letterSpacing: 1, marginBottom: 12 },

  layerGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 },
  layerCard: { background: '#0d1117', border: '1px solid #1c2333', borderRadius: 8, padding: 20 },
  layerName: { fontFamily: "'IBM Plex Mono', 'Courier New', monospace", fontSize: 13, color: '#3ddc84', marginBottom: 8 },
  layerDesc: { fontSize: 13, color: '#8b949e', lineHeight: 1.6 },

  eventTable: { width: '100%', borderCollapse: 'collapse', fontFamily: "'IBM Plex Mono', 'Courier New', monospace" },
  th: { textAlign: 'left', padding: '10px 16px', fontSize: 11, color: '#8b949e', borderBottom: '1px solid #1c2333', letterSpacing: 1, textTransform: 'uppercase' },
  td: { padding: '10px 16px', fontSize: 13, borderBottom: '1px solid #1c2333' },
  tdCode: { color: '#3ddc84' },
  tdDesc: { color: '#8b949e' },

  exposureCard: { background: '#111111', border: '1px solid #1c2333', borderRadius: 12, padding: 28, marginBottom: 20 },
  exposureZone: { fontFamily: "'IBM Plex Mono', 'Courier New', monospace", fontSize: 12, color: '#3ddc84', marginBottom: 8 },
  exposureTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: 16, color: '#c9d1d9', marginBottom: 10 },
  exposureBody: { fontSize: 14, color: '#8b949e', lineHeight: 1.7 },

  scoreBox: { background: '#0d1117', border: '1px solid #1c2333', borderRadius: 12, padding: 40, textAlign: 'center', marginBottom: 40 },
  scoreNum: { fontFamily: "'IBM Plex Mono', 'Courier New', monospace", fontSize: 64, color: '#3ddc84', lineHeight: 1 },
  scoreLabel: { fontSize: 13, color: '#8b949e', marginTop: 8 },
  scoreBody: { fontSize: 15, color: '#8b949e', lineHeight: 1.7, maxWidth: 680, margin: '0 auto' },

  compRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 40 },
  compCard: { background: '#111111', border: '1px solid #1c2333', borderRadius: 8, padding: 24, textAlign: 'center' },
  compName: { fontSize: 13, color: '#8b949e', marginBottom: 8 },
  compPrice: { fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 800, fontSize: 22, color: '#c9d1d9' },
  compNote: { fontSize: 12, color: '#8b949e', marginTop: 6 },

  faqItem: { borderBottom: '1px solid #1c2333', paddingBottom: 28, marginBottom: 28 },
  faqQ: { fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: 17, color: '#c9d1d9', marginBottom: 12 },
  faqA: { fontSize: 14, color: '#8b949e', lineHeight: 1.8 },

  ctaSection: { padding: '100px 24px', textAlign: 'center', background: '#080c12' },
  ctaH2: { fontSize: 'clamp(28px, 4vw, 44px)', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 800, color: '#c9d1d9', margin: '0 0 20px', maxWidth: 700, marginLeft: 'auto', marginRight: 'auto' },
  ctaBody: { fontSize: 16, color: '#8b949e', marginBottom: 40, maxWidth: 560, marginLeft: 'auto', marginRight: 'auto' },
  ctaNote: { fontSize: 13, color: '#8b949e', marginTop: 20 },
};

const EVENTS = [
  ['TANKER_DARK', 'Oil tanker goes off AIS grid'],
  ['AIS_SPOOF', 'Vessel transmitting falsified position data'],
  ['ZONE_ESCALATION', 'Conflict zone risk level increases'],
  ['CHOKEPOINT_BLOCKED', 'Critical shipping lane disrupted'],
  ['CYBER_ATTACK', 'Infrastructure or financial system cyber event'],
  ['DRONE_STRIKE', 'Confirmed drone strike in monitored zone'],
  ['GPS_JAM_ACTIVE', 'GPS jamming detected in risk zone'],
  ['PMC_DEPLOYMENT', 'Private military contractor movement recorded'],
  ['BIOSEC_OUTBREAK', 'Biosecurity event with supply chain implications'],
  ['DISINFO_SURGE', 'Coordinated disinformation campaign detected'],
  ['SOVEREIGN_DEFAULT', 'Government debt default or default risk trigger'],
  ['CAPITAL_CONTROLS', 'Government restricts capital movement'],
  ['ELECTION_UPSET', 'Election result diverges sharply from forecast'],
  ['REGIME_CHANGE', 'Government transition in monitored territory'],
];

const LAYERS = [
  { name: 'Conflict Zones', desc: '19 active and monitored geopolitical flashpoints, colour-coded by current risk severity from low tension to active conflict.' },
  { name: 'Oil and Gas Pipelines', desc: 'Global pipeline infrastructure overlaid on the map — so you can see which energy supply routes run through or adjacent to active risk zones.' },
  { name: 'Shipping Chokepoints', desc: 'The world\'s critical maritime chokepoints — Strait of Hormuz, Bab-el-Mandeb, Strait of Malacca, Taiwan Strait, Black Sea Straits — monitored for disruption signals.' },
  { name: 'Military Deployments', desc: 'Naval fleet positions and land force deployments in monitored zones, giving context to escalation patterns before they surface in the news.' },
  { name: 'Live AIS Tanker Tracking', desc: 'Real-time AIS data from AISStream shows the live position, course, and speed of oil tankers operating near risk zones. Dark events are flagged immediately.' },
  { name: 'Sanctions and Trade Restrictions', desc: 'Active sanctions regimes and trade restriction zones mapped geographically, with tagging for which asset classes and sectors are directly affected.' },
  { name: 'Election and Regime Risk', desc: 'High-stakes elections, political instability indicators, and regime change risk across emerging market and frontier economies with significant portfolio relevance.' },
  { name: 'Cyber and Disinformation Events', desc: 'Recorded cyber attack incidents and coordinated disinformation surges that have historically preceded or accompanied physical escalation events.' },
];

const EXPOSURES = [
  {
    zone: 'STRAIT OF HORMUZ',
    title: 'Energy stocks and global tech supply chains',
    body: 'Roughly 20% of global oil transits the Strait of Hormuz. Disruption here affects energy prices globally and creates supply chain pressure for hardware manufacturers. AAPL, NVDA, and AMZN all carry Hormuz exposure.',
  },
  {
    zone: 'TAIWAN STRAIT',
    title: 'Semiconductor-dependent stocks',
    body: 'Taiwan produces the majority of the world\'s advanced semiconductors. Escalation in the Taiwan Strait is the single largest geopolitical supply chain risk for companies like NVDA and AAPL. The Geo-Risk Map flags this exposure by position, not by asset class in aggregate.',
  },
  {
    zone: 'RED SEA / BAB-EL-MANDEB',
    title: 'Logistics, e-commerce, and consumer discretionary',
    body: 'The Houthi disruption of Red Sea shipping added weeks to global shipping times and billions in rerouting costs. AMZN and META both carry exposure. When Bab-el-Mandeb is flagged, logistics-dependent holdings are surfaced in your portfolio overlay.',
  },
  {
    zone: 'BLACK SEA / UKRAINE',
    title: 'Energy, materials, and agricultural commodity-linked equities',
    body: 'The Black Sea corridor is critical for energy and grain exports. Escalation affects commodity prices globally. Energy and materials sector holdings are flagged against active Black Sea and Ukraine zone activity.',
  },
];

const FAQS = [
  {
    q: 'What is geopolitical risk in investing?',
    a: 'Geopolitical risk refers to the potential for political events, conflicts, diplomatic crises, sanctions, or instability in specific regions to affect financial markets and the value of your investments. It includes military conflict, changes in government, trade restrictions, commodity supply disruptions caused by regional instability, and currency or capital controls. Unlike market risk, which is reflected in historical volatility, geopolitical risk is event-driven and often moves faster than traditional risk metrics can capture.',
  },
  {
    q: 'How does geopolitical risk affect stock prices?',
    a: 'Geopolitical events affect stock prices through several channels. Supply chain disruption raises costs and reduces revenue for companies dependent on affected regions or logistics routes. Energy price shocks, often triggered by events near major oil and gas chokepoints, flow through to virtually every sector. Sanctions and trade restrictions can remove entire markets from a company\'s addressable revenue base. Currency volatility in affected regions affects multinationals with local earnings. The impact is often asymmetric — escalation happens quickly, recovery takes longer.',
  },
  {
    q: 'How can retail investors track geopolitical risk?',
    a: 'Until recently, the honest answer was: they largely couldn\'t, not with any precision. Professional geo-risk data was reserved for institutional subscribers paying Bloomberg Terminal rates. The practical alternative for most retail investors was reading financial news — which, by definition, covers events after they have already influenced prices. Nuvport OS changes this. The Geo-Risk Map gives retail investors direct access to live conflict zone monitoring, AIS tanker tracking, event-driven alerts, and portfolio exposure overlays — tools that have no equivalent in the retail investment software market.',
  },
  {
    q: 'What is AIS tracking and why does it matter to investors?',
    a: 'AIS stands for Automatic Identification System. It is a radio transponder system carried by commercial vessels, including oil tankers, cargo ships, and bulk carriers, that broadcasts the vessel\'s identity, position, course, and speed. For investors, AIS is significant because live tanker movements reveal supply chain conditions before they are reported in the news. A tanker going "dark" — switching off its AIS transponder — in a contested zone is an intelligence signal, not just a navigation curiosity. Nuvport OS integrates live AIS data via AISStream to provide this signal layer inside the Geo-Risk Map.',
  },
  {
    q: 'Which stocks are most affected by geopolitical risk?',
    a: 'Exposure varies significantly by sector and specific holding. Energy stocks are directly exposed to any disruption near major oil and gas infrastructure or chokepoints like the Strait of Hormuz. Semiconductor and hardware technology companies — including NVDA and AAPL — carry significant Taiwan Strait exposure given Taiwan\'s dominance of advanced chip manufacturing. Logistics and e-commerce companies are exposed to Red Sea and Bab-el-Mandeb disruption. Materials and commodity-linked equities are sensitive to Black Sea and Eastern Europe instability. The most accurate answer is your specific portfolio, mapped to the zones that are active right now — which is exactly what the Geo-Risk Map shows you.',
  },
];

export default function GeoRiskMapPage() {
  useEffect(() => {
    document.title = 'Portfolio Geo-Risk Map | Nuvport OS';
    document.querySelector('meta[name="description"]')?.setAttribute('content',
      'Track geopolitical risk across 19 live conflict zones and see exactly how events in the Strait of Hormuz, Red Sea, or Taiwan Strait affect your portfolio. $29/month.'
    );
  }, []);

  return (
    <div style={S.page}>
      {/* Nav */}
      <nav style={S.nav}>
        <div style={S.navInner}>
          <a href="/" style={S.navLogo}>Nuvport OS</a>
          <a href="/" style={S.navLink}>← Back to terminal</a>
        </div>
      </nav>

      {/* Hero */}
      <section style={S.hero}>
        <div style={S.heroInner}>
          <div style={S.badge}>// GEO-RISK MAP — PRO MODULE</div>
          <h1 style={S.h1}>The World's First Portfolio Geo-Risk Map for Retail Investors.</h1>
          <p style={S.heroSub}>
            Institutional geo-risk intelligence has been locked behind a $2,000/month Bloomberg Terminal for decades.
            Nuvport OS breaks that open. Map your exact holdings against 19 live geopolitical conflict zones,
            40+ intelligence layers, and real-time AIS oil tanker tracking — all for $29/month.
          </p>
          <div style={S.statRow}>
            {['19 live conflict zones', '40+ intelligence layers', 'Live AIS tanker tracking', 'Portfolio exposure overlay'].map(s => (
              <div key={s} style={S.statItem}><span style={S.statDot} />{s}</div>
            ))}
          </div>
          <div style={S.ctaRow}>
            <a href="/signup?ref=geo-risk-map" style={S.ctaPrimary}>Start Free</a>
            <a href="#how-it-works" style={S.ctaSecondary}>See how it works</a>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section style={S.sectionAlt}>
        <div style={S.inner}>
          <div style={S.sectionLabel}>The Problem</div>
          <h2 style={S.h2}>Geo-risk data has always existed. It just wasn't meant for you.</h2>
          <div style={S.grid3}>
            <div style={S.card}>
              <div style={S.cardTitle}>The Bloomberg paywall.</div>
              <div style={S.cardBody}>Bloomberg Terminal subscribers get access to comprehensive geopolitical risk data. The cost: $24,000 per year. That is not a tool for retail investors. That is a moat designed to keep institutional edge inside institutional hands.</div>
            </div>
            <div style={S.card}>
              <div style={S.cardTitle}>By the time it's news, it's already priced in.</div>
              <div style={S.cardBody}>Reading about a Red Sea shipping disruption on a financial news site is not intelligence. It is a recap. Markets move on anticipation. Without a live monitoring layer, you are always reacting — never positioned.</div>
            </div>
            <div style={S.card}>
              <div style={S.cardTitle}>Beta and volatility don't measure this.</div>
              <div style={S.cardBody}>Standard risk metrics tell you how your portfolio behaved in the past. They cannot tell you that 38% of your tech holdings are exposed to Taiwan Strait escalation risk right now. Geopolitical shocks are event-driven. They require event-aware tools.</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" style={S.section}>
        <div style={S.inner}>
          <div style={S.sectionLabel}>How It Works</div>
          <h2 style={S.h2}>Here is how Nuvport OS maps geopolitical risk to your portfolio.</h2>
          <div style={S.grid3}>
            {[
              { step: 'STEP 01', title: 'Connect your portfolio.', body: 'Add your holdings to Nuvport OS. The Geo-Risk Map immediately overlays your specific tickers onto the relevant geopolitical zones. NVDA and AAPL flag Taiwan Strait exposure. AMZN flags Bab-el-Mandeb and Strait of Hormuz. Every position is pinned to the risk zones it actually touches.' },
              { step: 'STEP 02', title: 'Monitor 19 live conflict zones in real time.', body: 'Each of the 19 zones carries a live risk score derived from active intelligence layers — conflict intensity, shipping disruption data, military deployment signals, and AIS vessel movement. Scores update continuously. When a zone escalates, you see it immediately.' },
              { step: 'STEP 03', title: 'Receive event-driven alerts.', body: 'The event bus monitors for 14 classes of meaningful risk events: zone escalations, tanker dark events, chokepoint blockages, cyber attacks, drone strikes, GPS jamming, PMC deployments, and more. When an event fires, affected tickers in your portfolio are flagged instantly. No noise. No blanket alerts. Only what touches your holdings.' },
            ].map(({ step, title, body }) => (
              <div key={step} style={S.card}>
                <div style={S.stepNum}>{step}</div>
                <div style={S.cardTitle}>{title}</div>
                <div style={S.cardBody}>{body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intelligence Layers */}
      <section style={S.sectionAlt}>
        <div style={S.inner}>
          <div style={S.sectionLabel}>Intelligence Layers</div>
          <h2 style={S.h2}>40+ layers of geopolitical intelligence. One map.</h2>
          <p style={S.sectionIntro}>Most financial tools show you one thing at a time. The Geo-Risk Map renders every intelligence layer simultaneously — so you see the full picture, not a fragment of it.</p>
          <div style={S.layerGrid}>
            {LAYERS.map(({ name, desc }) => (
              <div key={name} style={S.layerCard}>
                <div style={S.layerName}>{name}</div>
                <div style={S.layerDesc}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Events */}
      <section style={S.section}>
        <div style={S.inner}>
          <div style={S.sectionLabel}>Event Bus</div>
          <h2 style={S.h2}>14 event types. Detected live. Mapped to your portfolio.</h2>
          <p style={S.sectionIntro}>The Geo-Risk Map event bus monitors for 14 distinct categories of geopolitical risk events. When any event fires, the system cross-references it against your holdings — and only alerts you if your portfolio has direct exposure.</p>
          <div style={{ overflowX: 'auto', border: '1px solid #1c2333', borderRadius: 8 }}>
            <table style={S.eventTable}>
              <thead>
                <tr>
                  <th style={S.th}>Event Type</th>
                  <th style={S.th}>Description</th>
                </tr>
              </thead>
              <tbody>
                {EVENTS.map(([code, desc]) => (
                  <tr key={code}>
                    <td style={{ ...S.td, ...S.tdCode }}>{code}</td>
                    <td style={{ ...S.td, ...S.tdDesc }}>{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Portfolio Exposure */}
      <section style={S.sectionAlt}>
        <div style={S.inner}>
          <div style={S.sectionLabel}>Portfolio Exposure</div>
          <h2 style={S.h2}>Your portfolio on the map.</h2>
          <p style={S.sectionIntro}>Generic geo-risk data tells you the world is unstable. The Geo-Risk Map tells you which of your specific holdings are exposed, and to which zones. That is the difference between a news feed and an intelligence tool.</p>
          <div style={S.grid2}>
            {EXPOSURES.map(({ zone, title, body }) => (
              <div key={zone} style={S.exposureCard}>
                <div style={S.exposureZone}>ZONE: {zone}</div>
                <div style={S.exposureTitle}>{title}</div>
                <div style={S.exposureBody}>{body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Geo-Risk Score */}
      <section style={S.section}>
        <div style={S.inner}>
          <div style={S.sectionLabel}>Geo-Risk Score</div>
          <h2 style={S.h2}>One number. The current temperature of the world.</h2>
          <div style={S.scoreBox}>
            <div style={S.scoreNum}>0 — 100</div>
            <div style={S.scoreLabel}>Composite Geo-Risk Index · Severity-weighted · Time-decay adjusted</div>
          </div>
          <p style={S.scoreBody}>
            The Geo-Risk Score aggregates all active events across all 19 zones, weighted by severity and adjusted with time decay — so a CHOKEPOINT_BLOCKED event carries more weight than a DISINFO_SURGE, and a three-week-old event contributes less than something that happened in the last 12 hours.
          </p>
          <br />
          <p style={S.scoreBody}>
            A score of 40 is baseline — the world is always at some level of geopolitical tension. A score climbing toward 70 or above signals material multi-zone escalation. The score is displayed in the module header at all times, so you always know where the world stands before you look at anything else.
          </p>
        </div>
      </section>

      {/* Competitive Context */}
      <section style={S.sectionAlt}>
        <div style={S.inner}>
          <div style={S.sectionLabel}>Competitive Context</div>
          <h2 style={S.h2}>The only retail geo-risk map in the world.</h2>
          <p style={S.sectionIntro}>This is not marketing language. It is a verifiable fact. Check every retail investment platform available today.</p>
          <div style={S.compRow}>
            {[
              { name: 'Bloomberg Terminal', price: '$24,000/yr', note: 'Institutional only' },
              { name: 'Koyfin', price: 'No geo-risk map', note: 'No portfolio zone overlay' },
              { name: 'TradingView', price: 'No geo-risk map', note: 'No AIS tracking' },
              { name: 'Nuvport OS', price: '$29/mo', note: 'Full geo-risk map included' },
            ].map(({ name, price, note }) => (
              <div key={name} style={name === 'Nuvport OS' ? { ...S.compCard, border: '1px solid #3ddc84' } : S.compCard}>
                <div style={S.compName}>{name}</div>
                <div style={{ ...S.compPrice, color: name === 'Nuvport OS' ? '#3ddc84' : '#c9d1d9' }}>{price}</div>
                <div style={S.compNote}>{note}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 14, color: '#8b949e', lineHeight: 1.7 }}>
            Nuvport OS is $29 per month. The Geo-Risk Map is included in every Pro subscription with no additional fee, no data add-on, and no seat-based pricing.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section style={S.section}>
        <div style={S.inner}>
          <div style={S.sectionLabel}>FAQ</div>
          <h2 style={S.h2}>Frequently asked questions.</h2>
          <div>
            {FAQS.map(({ q, a }) => (
              <div key={q} style={S.faqItem}>
                <div style={S.faqQ}>{q}</div>
                <div style={S.faqA}>{a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={S.ctaSection}>
        <h2 style={S.ctaH2}>The world does not stop moving. Neither should your intelligence.</h2>
        <p style={S.ctaBody}>Start free today. No credit card required. Full Pro access from day one, including the Geo-Risk Map, all 21 modules, and live event monitoring.</p>
        <div style={S.ctaRow}>
          <a href="/signup?ref=geo-risk-map" style={S.ctaPrimary}>Start Free</a>
        </div>
        <p style={S.ctaNote}>$29/month for Pro. Cancel any time. No Bloomberg prices required.</p>
      </section>
    </div>
  );
}
